import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { companyEmail, emailDeliveryLabel } from './companyEmail';

export default function BulkEmployeeInvitations({ company, employees }) {
  const [batch, setBatch] = useState(null), [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]), [done, setDone] = useState(false);
  const stopped = useRef(false), running = useRef(false), mounted = useRef(true);
  const dialog = useRef(null), trigger = useRef(null), titleId = useId();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; stopped.current = true; }; }, []);
  useEffect(() => { if (batch) dialog.current?.focus(); }, [batch]);
  function close() { if (running.current) return; setBatch(null); trigger.current?.focus(); }
  function keyboard(event) {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key === 'Tab') {
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled),summary')];
      if (!controls.length) { event.preventDefault(); return; }
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first.focus(); }
    }
  }
  async function send() {
    if (running.current) return;
    running.current = true; stopped.current = false; setBusy(true); setDone(false); setResults([]);
    const completed = [];
    try {
      for (const employee of batch) {
        if (stopped.current) break;
        let status, detail;
        if (employee.is_active === false || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employee.email || '')) {
          status = 'skipped'; detail = employee.is_active === false ? 'Inactive employee' : 'Save a valid employee email first';
        } else {
          try {
            const result = await companyEmail(company.id, 'send', { kind: 'welcome', employee_id: employee.id });
            if (result.skipped && result.reason === 'already_setup') { status = 'skipped'; detail = 'Password already set up'; }
            else if (result.duplicate && ['accepted', 'delivered'].includes(result.delivery?.status)) { status = 'already_sent'; detail = 'Invitation already sent'; }
            else if (result.duplicate || !['accepted', 'delivered'].includes(result.delivery?.status)) { status = 'review'; detail = emailDeliveryLabel(result.delivery?.status || 'unknown'); }
            else { status = 'accepted'; detail = 'Accepted by email service'; }
          } catch (error) { status = 'failed'; detail = error.message; }
        }
        completed.push({ id: employee.id, name: employee.name || employee.email || 'Employee', status, detail });
        if (mounted.current) setResults([...completed]);
      }
    } finally {
      running.current = false;
      if (mounted.current) { setBusy(false); setDone(true); }
    }
  }
  const counts = Object.fromEntries(['accepted', 'already_sent', 'skipped', 'failed', 'review'].map(status => [status, results.filter(item => item.status === status).length]));
  return <>
    <button ref={trigger} type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => { setBatch([...new Map(employees.map(employee => [employee.id, { ...employee }])).values()]); setResults([]); setDone(false); }}>Send setup invitations ({employees.length})</button>
    {batch && createPortal(<div className="overlay" onClick={close}>
      <section ref={dialog} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={keyboard} onClick={event => event.stopPropagation()} style={{ width:'min(560px, calc(100vw - 32px))', maxHeight:'85vh', overflowY:'auto', padding:24 }}>
        <h2 id={titleId} style={{fontSize:18,margin:'0 0 12px'}}>Send employee setup invitations</h2>
        <p style={{fontSize:13,lineHeight:1.5}}>Send individual password setup links to {batch.length} selected employees in {company.name}. Employees who already set their password will be skipped. Passwords are not reset.</p>
        <p style={{fontSize:12,color:'#475569',lineHeight:1.5}}>Inactive employees and missing email addresses are skipped. Pending employees can be invited again; recent duplicate sends are skipped automatically.</p>
        {(busy || done) && <div role="status" aria-live="polite" style={{fontSize:13,lineHeight:1.7,margin:'12px 0'}}>
          {busy ? 'Sending' : 'Finished'} · {results.length} / {batch.length} processed<br/>
          {counts.accepted} accepted · {counts.already_sent} already sent · {counts.skipped} skipped · {counts.failed} failed · {counts.review} need review
          {done && results.length < batch.length && <div>{batch.length - results.length} not processed.</div>}
        </div>}
        {!!results.length && <details style={{fontSize:12,margin:'12px 0'}}><summary>Employee results</summary><ul style={{paddingLeft:18,lineHeight:1.6}}>{results.map(item => <li key={item.id}><strong>{item.name}:</strong> {item.detail}</li>)}</ul></details>}
        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:16}}>
          {!busy && <button type="button" className="btn btn-ghost" onClick={close}>{done ? 'Close' : 'Cancel'}</button>}
          {!busy && !done && <button type="button" className="btn btn-primary" onClick={send}>Send invitations</button>}
          {busy && <button type="button" className="btn btn-ghost" onClick={() => { stopped.current = true; }}>Stop after current employee</button>}
        </div>
      </section>
    </div>, document.body)}
  </>;
}
